import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import styles from './TenantEmailBannerSection.scss';
import { Button, Icon, XS } from 'design-system-web';
import { H4 } from 'components/typography';
import { ViewTenantEmailBanner } from './ViewTenantEmailBanner';
import { EditTenantEmailBanner } from './EditTenantEmailBanner';
import { Flex, Tooltip } from 'components';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';
import { getClient } from 'lane-shared/apollo';
import {
  getVisitorEmailTemplate,
  upsertVisitorEmailTemplate,
} from 'lane-shared/graphql/visitorManagement';
import { EmailTemplateType } from 'lane-shared/domains/visitorManagement/types';
import { convertToUUID } from 'uuid-encoding';

type TenantEmailBannerSectionProps = {
  channel: { _id?: string } | undefined;
  tenantChannelId: string;
};

export const TenantEmailBannerSection = ({
  channel,
  tenantChannelId,
}: TenantEmailBannerSectionProps) => {
  const { t } = useTranslation();
  const [isEditMode, setIsEditMode] = useState(false);

  const { data: emailTemplateData, refetch } = useQuery(
    getVisitorEmailTemplate,
    {
      variables: {
        channelId: convertToUUID(tenantChannelId),
        templateType: EmailTemplateType.INVITE,
      },
      fetchPolicy: 'network-only',
      skip: !tenantChannelId,
    }
  );

  const backgroundId =
    emailTemplateData?.visitorEmailTemplate?.backgroundId ?? null;

  const handleSave = async (selectedBannerId: string | null) => {
    if (!tenantChannelId) return;

    try {
      await getClient().mutate({
        mutation: upsertVisitorEmailTemplate,
        variables: {
          emailTemplate: {
            organizationId: convertToUUID(tenantChannelId),
            templateType: EmailTemplateType.INVITE,
            backgroundId: selectedBannerId || '',
          },
        },
      });

      await refetch();
      setIsEditMode(false);
    } catch (err) {
      window.Alert.alert({
        title: t(
          'web.admin.channel.settings.tenantManagement.tenant.details.tenantEmailBannerSection.updateError'
        ),
        error: err,
      });
    }
  };

  return (
    <div data-test="tenant-email-banner-section">
      <div>
        <div className={styles.SectionHeader}>
          <Flex align="center">
            <H4 className={styles.Title}>
              {t(
                'web.admin.channel.settings.tenantManagement.tenant.details.tenantEmailBannerSection.title'
              )}
            </H4>
            <Tooltip
              TooltipComponent={
                <>{t`web.admin.channel.settings.tenantManagement.tenant.details.tenantEmailBannerSection.tooltip`}</>
              }
              placement="right"
            >
              <Icon
                className="cursor-pointer"
                name="info-circle"
                type="far"
                set={ICON_SET_FONTAWESOME}
                size="medium"
              />
            </Tooltip>
          </Flex>
          {!isEditMode && (
            <Button
              variant="secondary"
              size="medium"
              onClick={() => setIsEditMode(true)}
              testId="edit-tenant-email-banner"
            >
              {t(
                'web.admin.channel.settings.tenantManagement.tenant.details.tenantEmailBannerSection.edit'
              )}
            </Button>
          )}
        </div>
          <XS className={styles.description}>
            {t(
              'web.admin.channel.settings.tenantManagement.tenant.details.tenantEmailBannerSection.description'
            )}
          </XS>
      </div>
      {isEditMode ? (
        <EditTenantEmailBanner
          channel={channel}
          initialVisitorEmailBannerId={backgroundId}
          onSave={handleSave}
          onCancel={() => setIsEditMode(false)}
        />
      ) : (
        <ViewTenantEmailBanner visitorEmailBannerId={backgroundId} />
      )}
    </div>
  );
};
